import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class SalesArea extends ModelObjectWithDirtyCheck<SalesArea> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Sales Area Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Sales Area Description
    * Not Nullable
    * Max Length : 1000
    */
    public description: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.description = jsonSerializedObject.description;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.description = this.description;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "SalesArea.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "description", rule: (v: any) => v == null || v.length <= 1000 || "Description has too many characters (1000)"},
    ];
}


export class SalesAreaService extends ServiceBase {

  public async getAllSalesAreas(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesArea)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.salesAreas.map((v: any) => new SalesArea(v));;
    return this.makeJsonRequest("salesArea/v1/getAllSalesAreas", req, false, extractor, opts);
  };

  public async getSalesArea(salesAreaId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<SalesArea> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesAreaId:salesAreaId.toString(),
    };
    const extractor = (response:any) => new SalesArea(response.salesArea);;
    return this.makeJsonRequest("salesArea/v1/getSalesArea", req, false, extractor, opts);
  };

  public async getSalesAreas(salesAreaIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesArea)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesAreaIds:salesAreaIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.salesAreas.map((v: any) => new SalesArea(v));;
    return this.makeJsonRequest("salesArea/v1/getSalesAreas", req, false, extractor, opts);
  };

  public async saveSalesAreas(salesAreas: readonly SalesArea[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesAreas:salesAreas.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("salesArea/v1/saveSalesAreas", req, true, extractor, opts);
  };

  public async setSalesAreaIsHidden(salesAreaId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesAreaId:salesAreaId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("salesArea/v1/setSalesAreaIsHidden", req, true, extractor, opts);
  };
}

export const salesAreaService = new SalesAreaService();